/**
 * Carbon's five breakpoints, mirrored in TypeScript.
 *
 * The values live in Carbon's `$breakpoints` map (`@carbon/react/scss/breakpoint`)
 * and are what its grid and UI Shell switch on. Anything in TS that has to agree
 * with a CSS breakpoint — media-query hooks, chart sizing, table/card switching —
 * reads them from here, so there is one place to change if Carbon ever moves them.
 *
 * Carbon expresses these in rem against a 16px root; the px values below are the
 * rem values × 16, which is what `matchMedia` needs.
 *
 * Do not add project-specific breakpoints to this map. A layout that seems to
 * need one usually needs a different column span at an existing breakpoint.
 */
export const BREAKPOINTS = {
  /** 20rem — 4 columns. */
  sm: 320,
  /** 42rem — 8 columns. */
  md: 672,
  /** 66rem — 16 columns. */
  lg: 1056,
  /** 82rem — 16 columns, wider gutters. */
  xlg: 1312,
  /** 99rem — 16 columns, content stops growing past this. */
  max: 1584,
} as const;

export type BreakpointName = keyof typeof BREAKPOINTS;

/** `(min-width: …)` for a breakpoint — the mobile-first direction Carbon's own mixins use. */
export function above(name: BreakpointName): string {
  return `(min-width: ${BREAKPOINTS[name]}px)`;
}

/**
 * `(max-width: …)` for everything strictly below a breakpoint. The 0.02px step
 * avoids the gap that `max-width: 671px` leaves on fractional-pixel viewports,
 * where a 671.5px window matches neither this nor `above()`.
 */
export function below(name: BreakpointName): string {
  return `(max-width: ${BREAKPOINTS[name] - 0.02}px)`;
}
